// ─── Formulaire d'ÉVÉNEMENT (création / édition / série) ──────────
// Extrait d'EcranCalendrier (découpage). L'ÉTAT (form, erreurs, picker) reste
// au parent — ce composant rend la feuille + les sélecteurs date/heure sœurs.
// Erreurs (principe app-wide) : champs rouges + LigneErreur + disquette 3 états.
import React, { useMemo } from 'react';
import { View, Text, TextInput, TouchableOpacity, Switch, StyleSheet } from 'react-native';
import {
  Feuille, SelecteurDate, SelecteurHeure, PiecesJointes, LigneErreur, useUI, useT,
} from '@mop/core';
import { JOURS_SEM, CHAMPS_EVENEMENT } from './services/evenements';
import ChampsPerso from './ChampsPerso';
import { CATEGORIES_EVT, emojiCategorie, labelCategorie } from './data/categories';

// « Tous les 15 j » n'est plus une puce : c'est Semaine + intervalle 2 (stocké
// `bimensuel` pour rester lisible par MoPCal). intervalle k>2 → hebdo+intervalle.
const REPETS = [
  { id: '' }, { id: 'quotidien' }, { id: 'hebdo' }, { id: 'mensuel' },
];
const fmtJour = (dk) => { if (!/^\d{4}-\d{2}-\d{2}$/.test(dk || '')) return '—'; const [y, m, d] = dk.split('-'); return `${d}/${m}/${y}`; };

export default function FormulaireEvenement({
  form, setForm, formErr, errDates, errHeures, heureFinManquante,
  formModifie, formEnErreur, busy, enregistrer, montrerErreurs, estompe, CE,
  posErr, scrollForm, picker, setPicker, ouvrirPicker, finParDefaut,
  repetCourt, toggleJour, joursL, calId, setChoixLieuForm, onFermer, onDemanderDate,
}) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);

  return (
    <>
      <Feuille visible={!!form} titre={(form?.id || form?.recId) ? t('calendrier.modifierEvenement') : t('calendrier.nouvelEvenement')} emoji="📅"
        hauteur={0.82} scrollRef={scrollForm}
        onFermer={onFermer}
        action={{ icone: (form?.id || form?.recId) ? '💾' : '＋', onPress: enregistrer, disabled: !formModifie || busy, label: t('commun.enregistrer'),
          enErreur: formEnErreur, onErreur: montrerErreurs }}>
        {!!formErr && <Text style={s.formErr}>⚠️ {formErr}</Text>}
        <Text style={s.lbl}>{t('ft.labelTitre')}</Text>
        <TextInput style={s.input} value={form?.titre || ''} autoFocus={!form?.id && !form?.recId}
          onChangeText={(v) => setForm(x => ({ ...x, titre: v }))}
          placeholder={t('calendrier.phTitreEvt')} placeholderTextColor={UI.faint} />

        <Text style={s.lbl}>{t('calendrier.categorieOptionnel')}</Text>
        <View style={s.puces}>
          <TouchableOpacity style={[s.catChip, !form?.categorie && s.puceOn]} onPress={() => setForm(x => ({ ...x, categorie: '' }))} accessibilityLabel={t('calendrier.aucuneCategorie')}>
            <Text style={[s.catChipTxt, { fontSize: 15, color: UI.muted }]}>–</Text>
          </TouchableOpacity>
          {CATEGORIES_EVT.map(cat => {
            const on = form?.categorie === cat.id;
            return (
              <TouchableOpacity key={cat.id} style={[s.catChip, on && s.puceOn]} onPress={() => setForm(x => ({ ...x, categorie: cat.id }))} accessibilityLabel={labelCategorie(cat.id, t)}>
                <Text style={s.catChipTxt}>{emojiCategorie(cat.id)}</Text>
              </TouchableOpacity>
            );
          })}
        </View>

        <View style={{ flexDirection: 'row', gap: 10 }} onLayout={(e) => { posErr.current.dates = e.nativeEvent.layout.y; }}>
          <View style={{ flex: 1 }}>
            <Text style={[s.lbl, errDates && { color: UI.danger }]}>{t('calendrier.dateDebut')}</Text>
            <TouchableOpacity style={[s.champBtn, errDates && CE, estompe(errDates)]} onPress={() => ouvrirPicker({ type: 'date', champ: 'dk', titre: t('calendrier.dateDebut') })}>
              <Text style={s.champVal}>📅 {fmtJour(form?.dk)}</Text>
            </TouchableOpacity>
          </View>
          <View style={{ flex: 1 }}>
            <Text style={[s.lbl, errDates && { color: UI.danger }]}>{form?.repet ? t('calendrier.repeterJusquau') : t('finances.dateFin')}</Text>
            <TouchableOpacity style={[s.champBtn, errDates && CE, estompe(errDates)]} onPress={() => ouvrirPicker({ type: 'date', champ: 'dateFinEvt', titre: form?.repet ? t('calendrier.repeterJusquau') : t('finances.dateFin'), avecEffacer: true })}>
              <Text style={[s.champVal, !form?.dateFinEvt && s.champVide]}>📅 {form?.dateFinEvt ? fmtJour(form.dateFinEvt) : (form?.repet ? t('calendrier.sansFin') : t('calendrier.memeJour'))}</Text>
            </TouchableOpacity>
          </View>
        </View>
        {errDates && <LigneErreur texte={t('erreurs.dateFinAvantDebut')} />}
        <View style={s.switchRow}>
          <Text style={s.switchLbl}>🗓️ {t('calendrier.jourEntier')}</Text>
          <Switch value={!!form?.jourEntier}
            onValueChange={(v) => setForm(x => ({ ...x, jourEntier: v, ...(v ? { heure: '', heureFin: '' } : {}) }))}
            trackColor={{ false: UI.champBord, true: UI.accent }} thumbColor="#fff" />
        </View>
        {!form?.jourEntier && (
          <View style={{ flexDirection: 'row', gap: 10 }} onLayout={(e) => { posErr.current.heures = e.nativeEvent.layout.y; }}>
            <View style={{ flex: 1 }}>
              <Text style={[s.lbl, errHeures && { color: UI.danger }]}>{t('calendrier.heureDebut')}</Text>
              <TouchableOpacity style={[s.champBtn, errHeures && CE, estompe(errHeures)]} onPress={() => ouvrirPicker({ type: 'heure', champ: 'heure', titre: t('calendrier.heureDebut') })}>
                <Text style={[s.champVal, !form?.heure && s.champVide]}>🕒 {form?.heure || '—'}</Text>
              </TouchableOpacity>
            </View>
            <View style={{ flex: 1 }}>
              <Text style={[s.lbl, (errHeures || heureFinManquante) && { color: UI.danger }]}>{t('calendrier.heureFin')}</Text>
              <TouchableOpacity style={[s.champBtn, (errHeures || heureFinManquante) && CE, estompe(errHeures || heureFinManquante)]} onPress={() => ouvrirPicker({ type: 'heure', champ: 'heureFin', titre: t('calendrier.heureFin') })}>
                <Text style={[s.champVal, !form?.heureFin && s.champVide]}>🕒 {form?.heureFin || '—'}</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}
        {errHeures && <LigneErreur texte={t('erreurs.heureFinAvantDebut')} />}
        {heureFinManquante && <LigneErreur texte={t('calendrier.heureFinRequise')} />}
        {/* Emplacement : un LIEU du calendrier (menu appui long → Lieux). */}
        <Text style={s.lbl}>{t('edt.emplacement')}</Text>
        <TouchableOpacity style={s.champBtn} onPress={() => setChoixLieuForm(true)}>
          <Text style={[s.champVal, !form?.lieu && s.champVide]} numberOfLines={1}>📍 {form?.lieu || t('lieux.aucun')}</Text>
        </TouchableOpacity>
        {/* Champs « a la carte » : meme composant et memes gestes que les
            contacts, seul le catalogue change. Un champ ajoute puis laisse vide
            n'est pas conserve. */}
        <ChampsPerso catalogue={CHAMPS_EVENEMENT} entries={form?.champs || []}
          onChange={(l) => setForm(x => ({ ...x, champs: l }))}
          onDemanderDate={onDemanderDate} />

        <Text style={s.lbl}>{t('formJour.notePlaceholder')}</Text>
        <TextInput style={[s.input, { height: 80, textAlignVertical: 'top' }]} multiline value={form?.note || ''}
          onChangeText={(v) => setForm(x => ({ ...x, note: v }))}
          placeholder={t('calendrier.phNote')} placeholderTextColor={UI.faint} />

        <Text style={s.lbl}>{t('calendrier.piecesJointesOptionnel')}</Text>
        <PiecesJointes valeur={form?.pieces || []} dossier={`evenements/${form?.calEvt || calId}`}
          onChange={(l) => setForm(x => ({ ...x, pieces: l }))} />

        {/* Répétition — à la création, ET à l'édition d'un événement SIMPLE :
            choisir une répétition TRANSFORME l'événement en série récurrente. */}
        {!form?.recId && (
          <>
            <Text style={s.lbl}>{t('calendrier.repetition')}</Text>
            <View style={s.puces}>
              {REPETS.map(r => (
                <TouchableOpacity key={r.id} style={[s.puce, form?.repet === r.id && s.puceOn]}
                  onPress={() => setForm(x => ({ ...x, repet: r.id, intervalle: x.repet === r.id ? x.intervalle : 1 }))}>
                  <Text style={[s.puceTxt, form?.repet === r.id && s.puceTxtOn]}>{repetCourt(r.id)}</Text>
                </TouchableOpacity>
              ))}
            </View>
            {/* Fréquence : toutes les [−] k [+] semaines / tous les [−] k [+] mois. */}
            {['quotidien', 'hebdo', 'mensuel'].includes(form?.repet) && (
              <View style={s.intervRow}>
                <Text style={s.intervTxt}>{form?.repet === 'hebdo' ? t('calendrier.toutesLes') : t('calendrier.tousLes')}</Text>
                <TouchableOpacity style={s.intervBtn} onPress={() => setForm(x => ({ ...x, intervalle: Math.max(1, (Number(x.intervalle) || 1) - 1) }))} accessibilityLabel="−">
                  <Text style={s.intervBtnTxt}>−</Text>
                </TouchableOpacity>
                <Text style={s.intervVal}>{Number(form?.intervalle) || 1}</Text>
                <TouchableOpacity style={s.intervBtn} onPress={() => setForm(x => ({ ...x, intervalle: Math.min(52, (Number(x.intervalle) || 1) + 1) }))} accessibilityLabel="＋">
                  <Text style={s.intervBtnTxt}>＋</Text>
                </TouchableOpacity>
                <Text style={s.intervTxt}>{form?.repet === 'hebdo' ? t('calendrier.suffSemaines') : (form?.repet === 'quotidien' ? t('calendrier.suffJours') : t('calendrier.suffMois'))}</Text>
              </View>
            )}
            {['hebdo', 'bimensuel', 'mensuel'].includes(form?.repet) && (
              <>
                <Text style={s.lbl}>{t('calendrier.jours')}</Text>
                <View style={s.puces}>
                  {JOURS_SEM.map((j, ji) => (
                    <TouchableOpacity key={j.i} style={[s.jourChip, form?.joursSemaine?.includes(j.i) && s.puceOn]} onPress={() => toggleJour(j.i)}>
                      <Text style={[s.puceTxt, form?.joursSemaine?.includes(j.i) && s.puceTxtOn]}>{joursL[ji]}</Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {/* Règle appliquée, pour lever l'ambiguïté semaines / mensuel. */}
                {form?.repet === 'hebdo' && (Number(form?.intervalle) || 1) > 1 && (
                  <Text style={s.aideRepet}>{t('calendrier.aideSemainesJours', { n: Number(form?.intervalle) || 1 })}</Text>
                )}
                {form?.repet === 'mensuel' && (
                  <Text style={s.aideRepet}>{(form?.joursSemaine?.length
                    ? t('calendrier.aideMensuelJours', { n: Math.ceil(Number((form?.dk || '').split('-')[2] || 1) / 7) })
                    : t('calendrier.aideMensuelSimple'))
                    + ((Number(form?.intervalle) || 1) > 1 ? ' ' + t('calendrier.aideTousLesNMois', { n: Number(form?.intervalle) || 1 }) : '')}</Text>
                )}
              </>
            )}
          </>
        )}
      </Feuille>

      {/* Sélecteurs date / heure (modales sœurs — nesting natif fiable).
          `min` grise l'impossible : date/heure de fin ≥ début (le même jour). */}
      <SelecteurDate visible={picker?.type === 'date'} titre={picker?.titre} avecEffacer={picker?.avecEffacer}
        valeur={picker ? (form?.[picker.champ] || '') : ''}
        min={(picker?.champ === 'dateFinEvt' || picker?.champ === 'dateFin') ? form?.dk : undefined}
        onChange={(dk) => setForm(x => ({ ...x, [picker.champ]: dk }))}
        onFermer={() => setPicker(null)} />
      <SelecteurHeure visible={picker?.type === 'heure'} titre={picker?.titre} avecEffacer={picker?.avecEffacer}
        valeur={picker ? (form?.[picker.champ] || '') : ''}
        min={(picker?.champ === 'heureFin' && (!form?.dateFinEvt || form?.dateFinEvt === form?.dk)) ? form?.heure : undefined}
        onChange={(hhmm) => setForm(x => {
          const nx = { ...x, [picker.champ]: hhmm };
          // Choisir une heure de début pré-remplit l'heure de fin (durée par défaut)
          // si elle est vide → jamais d'événement horaire sans heure de fin.
          if (picker.champ === 'heure' && hhmm && !(x.heureFin || '').trim()) nx.heureFin = finParDefaut(hhmm);
          return nx;
        })}
        onFermer={() => setPicker(null)} />
    </>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  formErr: { fontSize: 12.5, color: '#C62828', fontWeight: '700', backgroundColor: '#FFEBEE', borderRadius: 9, padding: 10, marginBottom: 4 },
  lbl: { fontSize: 12, fontWeight: '700', color: UI.muted, marginTop: 12, marginBottom: 7 },
  input: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 11, fontSize: 15, color: UI.ink, borderWidth: 0.5, borderColor: UI.champBord },
  champBtn: { backgroundColor: UI.champ, borderRadius: 10, paddingHorizontal: 12, paddingVertical: 12, borderWidth: 0.5, borderColor: UI.champBord },
  champVal: { fontSize: 15, color: UI.ink, fontWeight: '600' },
  champVide: { color: UI.faint, fontWeight: '400' },
  puces: { flexDirection: 'row', flexWrap: 'wrap', gap: 7 },
  puce: { paddingHorizontal: 12, paddingVertical: 8, borderRadius: 999, backgroundColor: UI.champ, borderWidth: 0.5, borderColor: UI.champBord },
  jourChip: { width: 38, height: 38, borderRadius: 999, alignItems: 'center', justifyContent: 'center', backgroundColor: UI.champ, borderWidth: 0.5, borderColor: UI.champBord },
  puceOn: { backgroundColor: UI.accentTint, borderColor: UI.accent },
  puceTxt: { fontSize: 12.5, color: UI.ink, fontWeight: '600' },
  puceTxtOn: { color: UI.accent, fontWeight: '800' },
  catChip: { width: 42, height: 42, borderRadius: 11, alignItems: 'center', justifyContent: 'center', backgroundColor: UI.champ, borderWidth: 0.5, borderColor: UI.champBord },
  catChipTxt: { fontSize: 20 },
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 14, paddingVertical: 4 },
  switchLbl: { fontSize: 14, fontWeight: '700', color: UI.ink },
  intervRow: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 2, marginBottom: 8 },
  intervBtn: { width: 32, height: 32, borderRadius: 16, borderWidth: 1, borderColor: UI.champBord, backgroundColor: UI.card, alignItems: 'center', justifyContent: 'center' },
  intervBtnTxt: { fontSize: 17, color: UI.accent, fontWeight: '700' },
  intervVal: { fontSize: 16, fontWeight: '800', color: UI.ink, minWidth: 24, textAlign: 'center' },
  intervTxt: { fontSize: 13.5, color: UI.muted },
  aideRepet: { fontSize: 12, color: UI.muted, marginTop: 2, marginBottom: 6 },
});
